import type { I18nKey } from '../../i18n/index.ts';

/**
 * Static shape of the org admin API reference.
 *
 * Paths, params, and fields follow `v1-admin.ts`, `v1-admin-openapi.ts`,
 * and `org-admin-key.ts`. Prose lives in the i18n catalogs; this module
 * only names fields and the curl skeleton. Curl examples substitute the
 * runtime platform origin and `$NXTPATH_ADMIN_KEY` — never a real key.
 */

export interface AdminField {
  readonly name: string;
  readonly detailKey: I18nKey;
}

export interface AdminEndpoint {
  readonly id: string;
  readonly method: 'GET' | 'PATCH';
  readonly path: string;
  readonly summaryKey: I18nKey;
  readonly highlightKey?: I18nKey;
  readonly noteKey?: I18nKey;
  readonly query: readonly AdminField[];
  readonly body: readonly AdminField[];
  readonly response: readonly AdminField[];
  /** Path and example query, appended to `{origin}/v1/admin`. */
  readonly curlPath: string;
  readonly curlBody?: string;
}

const limit: AdminField = { name: 'limit', detailKey: 'adminApi.doc.f.limit' };
const offset: AdminField = { name: 'offset', detailKey: 'adminApi.doc.f.offset' };
const from: AdminField = { name: 'from', detailKey: 'adminApi.doc.f.from' };
const to: AdminField = { name: 'to', detailKey: 'adminApi.doc.f.to' };
const accountId: AdminField = { name: 'account_id', detailKey: 'adminApi.doc.f.account_id' };
const modelRejected: AdminField = { name: 'model', detailKey: 'adminApi.doc.f.model_rejected' };
const modelOk: AdminField = { name: 'model', detailKey: 'adminApi.doc.f.model_ok' };
const tz: AdminField = { name: 'tz', detailKey: 'adminApi.doc.f.tz' };
const hasMore: AdminField = { name: 'has_more', detailKey: 'adminApi.doc.f.has_more' };
const nextOffset: AdminField = { name: 'next_offset', detailKey: 'adminApi.doc.f.next_offset' };
const pageData: AdminField = { name: 'data', detailKey: 'adminApi.doc.f.page_data' };
const micros: AdminField = { name: 'usd_micros', detailKey: 'adminApi.doc.f.money.micros' };
const cents: AdminField = { name: 'usd_cents', detailKey: 'adminApi.doc.f.money.cents' };
const decimal: AdminField = { name: 'usd', detailKey: 'adminApi.doc.f.money.decimal' };

function money(prefix: string): readonly AdminField[] {
  return [
    { name: `${prefix}_usd_micros`, detailKey: micros.detailKey },
    { name: `${prefix}_usd_cents`, detailKey: cents.detailKey },
    { name: `${prefix}_usd`, detailKey: decimal.detailKey },
  ];
}

const memberFields: readonly AdminField[] = [
  { name: 'account_id', detailKey: 'adminApi.doc.f.member.account_id' },
  { name: 'email', detailKey: 'adminApi.doc.f.member.email' },
  { name: 'display_name', detailKey: 'adminApi.doc.f.member.display_name' },
  { name: 'role', detailKey: 'adminApi.doc.f.member.role' },
  { name: 'status', detailKey: 'adminApi.doc.f.member.status' },
  { name: 'monthly_limit_usd_cents', detailKey: 'adminApi.doc.f.member.limit' },
  { name: 'joined_at', detailKey: 'adminApi.doc.f.member.joined_at' },
];

function nest(prefix: string, fields: readonly AdminField[]): readonly AdminField[] {
  return fields.map((field) => ({ name: `${prefix}${field.name}`, detailKey: field.detailKey }));
}

const windowQuery: readonly AdminField[] = [from, to];

export const ADMIN_ERROR_ENVELOPE = `{
  "error": {
    "code": "ADMIN_KEY_INVALID",
    "message": "org admin key is unknown or revoked",
    "retryable": false
  }
}`;

export const ADMIN_ENDPOINTS: readonly AdminEndpoint[] = [
  {
    id: 'me',
    method: 'GET',
    path: '/v1/admin/me',
    summaryKey: 'adminApi.doc.ep.me.summary',
    query: [],
    body: [],
    response: [
      { name: 'id', detailKey: 'adminApi.doc.f.me.id' },
      { name: 'name', detailKey: 'adminApi.doc.f.me.name' },
      { name: 'key_prefix', detailKey: 'adminApi.doc.f.me.prefix' },
      { name: 'org.id', detailKey: 'adminApi.doc.f.me.org_id' },
      { name: 'org.name', detailKey: 'adminApi.doc.f.me.org_name' },
      { name: 'created_by', detailKey: 'adminApi.doc.f.me.created_by' },
      { name: 'rate_limit.bucket', detailKey: 'adminApi.doc.f.me.bucket' },
      { name: 'rate_limit.limit', detailKey: 'adminApi.doc.f.me.limit' },
      { name: 'rate_limit.window_seconds', detailKey: 'adminApi.doc.f.me.window' },
    ],
    curlPath: '/me',
  },
  {
    id: 'members',
    method: 'GET',
    path: '/v1/admin/members',
    summaryKey: 'adminApi.doc.ep.members.summary',
    query: [{ name: 'status', detailKey: 'adminApi.doc.f.status_filter' }, limit, offset],
    body: [],
    response: [pageData, ...nest('data[].', memberFields), hasMore, nextOffset],
    curlPath: '/members?limit=50',
  },
  {
    id: 'member',
    method: 'GET',
    path: '/v1/admin/members/{account_id}',
    summaryKey: 'adminApi.doc.ep.member.summary',
    query: [],
    body: [],
    response: memberFields,
    curlPath: '/members/acc_EXAMPLE',
  },
  {
    id: 'member-patch',
    method: 'PATCH',
    path: '/v1/admin/members/{account_id}',
    summaryKey: 'adminApi.doc.ep.member_patch.summary',
    highlightKey: 'adminApi.doc.ep.member_patch.highlight',
    query: [],
    body: [
      { name: 'monthly_limit_usd_cents', detailKey: 'adminApi.doc.f.patch.limit' },
      { name: 'role', detailKey: 'adminApi.doc.f.patch.role' },
      { name: 'status', detailKey: 'adminApi.doc.f.patch.status' },
    ],
    response: [
      { name: 'member', detailKey: 'adminApi.doc.f.patch.member' },
      ...nest('member.', memberFields),
      { name: 'cache.revision', detailKey: 'adminApi.doc.f.patch.revision' },
      { name: 'cache.convergence', detailKey: 'adminApi.doc.f.patch.convergence' },
      { name: 'cache.effective_after', detailKey: 'adminApi.doc.f.patch.effective_after' },
    ],
    curlPath: '/members/acc_EXAMPLE',
    curlBody: '{"monthly_limit_usd_cents":5000}',
  },
  {
    id: 'roles',
    method: 'GET',
    path: '/v1/admin/roles',
    summaryKey: 'adminApi.doc.ep.roles.summary',
    query: [],
    body: [],
    response: [
      { name: 'data[].id', detailKey: 'adminApi.doc.f.roles.id' },
      { name: 'data[].name', detailKey: 'adminApi.doc.f.roles.name' },
      { name: 'data[].builtin', detailKey: 'adminApi.doc.f.roles.builtin' },
      { name: 'data[].permissions', detailKey: 'adminApi.doc.f.roles.permissions' },
      { name: 'data[].member_count', detailKey: 'adminApi.doc.f.roles.member_count' },
      { name: 'has_more', detailKey: 'adminApi.doc.f.roles.has_more' },
      { name: 'next_cursor', detailKey: 'adminApi.doc.f.roles.next_cursor' },
    ],
    curlPath: '/roles',
  },
  {
    id: 'usage-summary',
    method: 'GET',
    path: '/v1/admin/usage/summary',
    summaryKey: 'adminApi.doc.ep.usage_summary.summary',
    query: [...windowQuery, modelRejected],
    body: [],
    response: [
      { name: 'org_id', detailKey: 'adminApi.doc.f.usage.org_id' },
      { name: 'total_requests', detailKey: 'adminApi.doc.f.usage.requests' },
      { name: 'total_input_tokens', detailKey: 'adminApi.doc.f.usage.input' },
      { name: 'total_output_tokens', detailKey: 'adminApi.doc.f.usage.output' },
      { name: 'total_cache_read_tokens', detailKey: 'adminApi.doc.f.usage.cache_read' },
      { name: 'total_cache_write_tokens', detailKey: 'adminApi.doc.f.usage.cache_write' },
      ...money('total_cost'),
      { name: 'from', detailKey: 'adminApi.doc.f.usage.window_from' },
      { name: 'to', detailKey: 'adminApi.doc.f.usage.window_to' },
    ],
    curlPath: '/usage/summary?from=2026-01-01&to=2026-01-31',
  },
  {
    id: 'usage-daily',
    method: 'GET',
    path: '/v1/admin/usage/daily',
    summaryKey: 'adminApi.doc.ep.usage_daily.summary',
    query: [...windowQuery, accountId, tz, modelRejected],
    body: [],
    response: [
      { name: 'timezone', detailKey: 'adminApi.doc.f.daily.timezone' },
      { name: 'days[].date', detailKey: 'adminApi.doc.f.daily.date' },
      { name: 'days[].requests', detailKey: 'adminApi.doc.f.usage.requests' },
      { name: 'days[].input_tokens', detailKey: 'adminApi.doc.f.usage.input' },
      { name: 'days[].output_tokens', detailKey: 'adminApi.doc.f.usage.output' },
      ...nest('days[].', money('cost')),
    ],
    curlPath: '/usage/daily?from=2026-01-01&to=2026-01-31',
  },
  {
    id: 'usage-members',
    method: 'GET',
    path: '/v1/admin/usage/members',
    summaryKey: 'adminApi.doc.ep.usage_members.summary',
    noteKey: 'adminApi.doc.ep.usage_members.note',
    query: [...windowQuery, accountId, modelOk, limit, offset],
    body: [],
    response: [
      pageData,
      { name: 'data[].member_account_id', detailKey: 'adminApi.doc.f.seats.account_id' },
      { name: 'data[].member_name', detailKey: 'adminApi.doc.f.seats.name' },
      { name: 'data[].email', detailKey: 'adminApi.doc.f.member.email' },
      { name: 'data[].status', detailKey: 'adminApi.doc.f.member.status' },
      { name: 'data[].requests', detailKey: 'adminApi.doc.f.usage.requests' },
      { name: 'data[].input_tokens', detailKey: 'adminApi.doc.f.usage.input' },
      { name: 'data[].output_tokens', detailKey: 'adminApi.doc.f.usage.output' },
      { name: 'data[].total_tokens', detailKey: 'adminApi.doc.f.seats.total_tokens' },
      ...nest('data[].', money('cost')),
      { name: 'data[].monthly_limit_usd_cents', detailKey: 'adminApi.doc.f.member.limit' },
      { name: 'data[].monthly_used_usd_cents', detailKey: 'adminApi.doc.f.seats.used' },
      { name: 'data[].monthly_remaining_usd_cents', detailKey: 'adminApi.doc.f.seats.remaining' },
      { name: 'data[].monthly_usage_ratio', detailKey: 'adminApi.doc.f.seats.ratio' },
      hasMore,
      nextOffset,
    ],
    curlPath: '/usage/members?from=2026-01-01&to=2026-01-31&limit=50',
  },
  {
    id: 'requests',
    method: 'GET',
    path: '/v1/admin/requests',
    summaryKey: 'adminApi.doc.ep.requests.summary',
    noteKey: 'adminApi.doc.ep.requests.note',
    query: [...windowQuery, accountId, modelRejected, limit, offset],
    body: [],
    response: [
      pageData,
      { name: 'data[].id', detailKey: 'adminApi.doc.f.requests.id' },
      { name: 'data[].request_id', detailKey: 'adminApi.doc.f.requests.request_id' },
      { name: 'data[].api_key_prefix', detailKey: 'adminApi.doc.f.requests.key_prefix' },
      { name: 'data[].member_account_id', detailKey: 'adminApi.doc.f.seats.account_id' },
      { name: 'data[].vendor', detailKey: 'adminApi.doc.f.requests.vendor' },
      { name: 'data[].model', detailKey: 'adminApi.doc.f.requests.model' },
      { name: 'data[].input_tokens', detailKey: 'adminApi.doc.f.usage.input' },
      { name: 'data[].output_tokens', detailKey: 'adminApi.doc.f.usage.output' },
      { name: 'data[].cache_read_tokens', detailKey: 'adminApi.doc.f.usage.cache_read' },
      { name: 'data[].cache_write_tokens', detailKey: 'adminApi.doc.f.usage.cache_write' },
      ...nest('data[].', money('cost')),
      { name: 'data[].status_code', detailKey: 'adminApi.doc.f.requests.status_code' },
      { name: 'data[].latency_ms', detailKey: 'adminApi.doc.f.requests.latency' },
      { name: 'data[].created_at', detailKey: 'adminApi.doc.f.requests.created_at' },
      hasMore,
      nextOffset,
    ],
    curlPath: '/requests?from=2026-01-01&to=2026-01-31&limit=50',
  },
  {
    id: 'billing-balance',
    method: 'GET',
    path: '/v1/admin/billing/balance',
    summaryKey: 'adminApi.doc.ep.balance.summary',
    noteKey: 'adminApi.doc.ep.balance.note',
    query: [],
    body: [],
    response: [
      { name: 'account_id', detailKey: 'adminApi.doc.f.balance.account_id' },
      { name: 'org_id', detailKey: 'adminApi.doc.f.usage.org_id' },
      ...money('balance'),
      { name: 'updated_at', detailKey: 'adminApi.doc.f.balance.updated_at' },
    ],
    curlPath: '/billing/balance',
  },
  {
    id: 'billing-transactions',
    method: 'GET',
    path: '/v1/admin/billing/transactions',
    summaryKey: 'adminApi.doc.ep.transactions.summary',
    noteKey: 'adminApi.doc.ep.transactions.note',
    query: [limit, offset, from, to],
    body: [],
    response: [
      pageData,
      { name: 'data[].id', detailKey: 'adminApi.doc.f.tx.id' },
      { name: 'data[].type', detailKey: 'adminApi.doc.f.tx.type' },
      ...nest('data[].', money('amount')),
      { name: 'data[].reason', detailKey: 'adminApi.doc.f.tx.reason' },
      { name: 'data[].actor_type', detailKey: 'adminApi.doc.f.tx.actor_type' },
      { name: 'data[].actor_user_id', detailKey: 'adminApi.doc.f.tx.actor_user_id' },
      { name: 'data[].external_request_id', detailKey: 'adminApi.doc.f.tx.external_request_id' },
      { name: 'data[].created_at', detailKey: 'adminApi.doc.f.requests.created_at' },
      hasMore,
      nextOffset,
    ],
    curlPath: '/billing/transactions?limit=50',
  },
  {
    id: 'billing-statements',
    method: 'GET',
    path: '/v1/admin/billing/statements',
    summaryKey: 'adminApi.doc.ep.statements.summary',
    noteKey: 'adminApi.doc.ep.statements.note',
    query: [limit, offset, from, to],
    body: [],
    response: [
      pageData,
      { name: 'data[].id', detailKey: 'adminApi.doc.f.statements.id' },
      { name: 'data[].period_start', detailKey: 'adminApi.doc.f.statements.period_start' },
      { name: 'data[].period_end', detailKey: 'adminApi.doc.f.statements.period_end' },
      ...nest('data[].', money('consumption')),
      ...nest('data[].', money('opening_ar')),
      ...nest('data[].', money('closing_ar')),
      ...nest('data[].', money('payments_applied')),
      {
        name: 'data[].cny_reference_closing_ar_cents',
        detailKey: 'adminApi.doc.f.statements.cny',
      },
      { name: 'data[].due_date', detailKey: 'adminApi.doc.f.statements.due_date' },
      { name: 'data[].status', detailKey: 'adminApi.doc.f.statements.status' },
      { name: 'data[].created_at', detailKey: 'adminApi.doc.f.requests.created_at' },
      { name: 'data[].closed_at', detailKey: 'adminApi.doc.f.statements.closed_at' },
      hasMore,
      nextOffset,
    ],
    curlPath: '/billing/statements?limit=50',
  },
];

export function adminApiCurl(base: string, endpoint: AdminEndpoint): string {
  const verb = endpoint.method === 'GET' ? '' : `-X ${endpoint.method} `;
  const lines = [
    `curl -sS ${verb}"${base}${endpoint.curlPath}" \\`,
    '  -H "Authorization: Bearer $NXTPATH_ADMIN_KEY"',
  ];
  if (endpoint.curlBody !== undefined) {
    const auth = lines[1];
    if (auth !== undefined) {
      lines[1] = `${auth} \\`;
    }
    lines.push('  -H "Content-Type: application/json" \\');
    lines.push(`  -d '${endpoint.curlBody}'`);
  }
  return lines.join('\n');
}
